import cytoscape from 'cytoscape';

export interface CytoscapeStylesheet {
  selector: string;
  style: Record<string, string | number | boolean>;
}

export interface Node {
  id: string;
  label: string;
  description?: string | null;
  value: string;
  weight: number;
  nodeType: NodeType;
  themes: string[];
  isSocialIssue?: boolean | null;
  isSelected?: boolean;
}

export interface Edge {
  id: string;
  source: string;
  target: string;
  weight: number;
  label?: string;
}

export interface NetworkData {
  nodes: Node[];
  edges: Edge[];
}

export type NodeIDs = string[];

export interface NetworkVisualizerProps {
  data: NetworkData;
  onNodeClick?: (node: Node) => void;
  searchedNodeId?: string | null;
  setCyRef?: (cy: cytoscape.Core) => void;
}

export enum LayoutType {
  COSE = 'cose',
  CONCENTRIC = 'concentric',
  BREADTHFIRST = 'breadthfirst',
  CIRCLE = 'circle',
  GRID = 'grid',
  RANDOM = 'random',
  THEME_CIRCULAR = 'theme-circular',
  PRESET = 'preset',
}
export const DEFAULT_LAYOUT = LayoutType.THEME_CIRCULAR;

export const LAYOUT_PRIORITY: LayoutType[] = [
  LayoutType.CONCENTRIC, // 4重円レイアウト
  LayoutType.BREADTHFIRST, // 階層表示（追加）
  LayoutType.CIRCLE, // 円形（代替レイアウト）
  LayoutType.COSE, // 力学モデル（複雑なネットワーク用）
  LayoutType.GRID, // グリッド（デバッグ用）
  LayoutType.RANDOM, // ランダム（テスト用）
  LayoutType.THEME_CIRCULAR, // テーマ円形（テーマ用）
];

export const LAYOUT_LABELS: Record<LayoutType, string> = {
  [LayoutType.CONCENTRIC]: '同心円',
  [LayoutType.CIRCLE]: '円形',
  [LayoutType.GRID]: 'グリッド',
  [LayoutType.COSE]: 'Cose',
  [LayoutType.RANDOM]: 'ランダム',
  [LayoutType.BREADTHFIRST]: '階層表示',
  [LayoutType.THEME_CIRCULAR]: 'テーマ円形',
  [LayoutType.PRESET]: 'プリセット',
} as const;

// NodeTypeの定義（as constで型安全に）
export const NodeType = {
  CROSS: 'cross',
  SOCIAL_ISSUE: 'social_issue',
  CROSS_ISSUE: 'cross_issue',
  NORMAL: 'normal',
  ASSIST: 'assist',
  THEME: 'theme',
  REGION_ECONOMIC_UNION: 'region/economic_union',
  COUNTRY: 'country',
  ORGANIZATION_TYPE: 'organization_type',
} as const;

export type NodeType = (typeof NodeType)[keyof typeof NodeType];

// NODE_TYPESの定義（NodeTypeをキーに持つオブジェクト）
// それぞれのノードタイプに対するレベルとラベルのvariantを定義
export const NODE_TYPES: Record<
  NodeType,
  { level: number; label: string; description: string }
> = {
  [NodeType.CROSS]: {
    level: 75,
    label: 'クロスタグ',
    description: 'クロスタグの説明',
  },
  [NodeType.SOCIAL_ISSUE]: {
    level: 25,
    label: 'イシュータグ',
    description: 'イシュータグの説明',
  },
  [NodeType.CROSS_ISSUE]: {
    level: 10,
    label: 'クロスイシュータグ',
    description: 'クロスイシュータグの説明',
  },
  [NodeType.NORMAL]: {
    level: 150,
    label: 'ノーマルタグ',
    description: 'ノーマルタグの説明',
  },
  [NodeType.ASSIST]: {
    level: 175,
    label: 'アシストタグ',
    description: 'アシストタグの説明',
  },
  [NodeType.THEME]: {
    level: 100,
    label: 'テーマタグ',
    description: 'テーマタグの説明',
  },
  [NodeType.REGION_ECONOMIC_UNION]: {
    level: 150,
    label: '地域経済連合タグ',
    description: '地域経済連合タグの説明',
  },
  [NodeType.COUNTRY]: {
    level: 150,
    label: '国タグ',
    description: '国タグの説明',
  },
  [NodeType.ORGANIZATION_TYPE]: {
    level: 150,
    label: '組織形態タグ',
    description: '組織形態タグの説明',
  },
} as const;

export const getNodeTypeName = (type: NodeType | undefined): string => {
  return NODE_TYPES[type as NodeType]?.label || 'その他トピックタグ';
};

// ノードタイプフィルターの設定
export type NodeTypeFilterSettings = {
  [key in NodeType]: boolean;
};

// デフォルトはアシストタグ以外取得
export const DEFAULT_NODE_TYPE_FILTERS: NodeTypeFilterSettings = {
  [NodeType.THEME]: true,
  [NodeType.CROSS]: true,
  [NodeType.SOCIAL_ISSUE]: true,
  [NodeType.NORMAL]: false,
  [NodeType.ASSIST]: true,
  [NodeType.CROSS_ISSUE]: true,
  [NodeType.REGION_ECONOMIC_UNION]: false, // backendで除外している
  [NodeType.COUNTRY]: false, // backendで除外している
  [NodeType.ORGANIZATION_TYPE]: false, // backendで除外している
};

// コントロールパネルを表示するか
export const CONTROL_PANEL_VISIBLE = false;

// ズーム関連の定数
export const ZOOM_STEP = 0.1;
export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 2.0;
